import React, { useCallback, useEffect, useRef, useState } from 'react';
import { addClasses, cn, mergeClasses } from '#lib/helpers/class.js';
import { useBool } from '#lib/hooks/use-boolean-state.js';
import Button from '#lib/components/button.jsx';
import { PhotoIcon } from '@heroicons/react/24/solid';
import { Icon } from '@iconify-icon/react';
import ImagePreview from '#components/images/image-preview.jsx';
import { GalleryPanel } from '#components/images/gallery-panel.jsx';
import { isFunction } from '#lib/utils/function.js';
import { Spinner } from '#lib/components/loading.jsx';

export function GalleryEditor(props) {
    const {
        helperText = 'Edit gallery',
        onAddImage,
        onDelete,
        gallery,
        isUploading,
        activeImage,
        setActiveImage,
        showPrevImage,
        showNextImage,
        showModal,
        modal,
        defaultText = _t.global.selectImage(),
        onMoveGallery,
        dayId,
        planId,
        roomId,
        template,
        onMoveGalleryPlace,
        location,
        type,
        placeId
    } = props;

    const hasFiles = gallery && gallery.length > 0;

    const activeIndex = activeImage ? gallery.findIndex((img) => img.id === activeImage?.id) : 0;

    const [isHovering, onMouseOver, onMouseOut] = useBool(false);
    const [isDragging, onDragEnter, onDragLeave] = useBool(false);

    const onChange = useCallback(
        (toUpload, files) => {
            onDragLeave();
            onAddImage(toUpload, files);
        },
        [onAddImage, onDragLeave]
    );

    const handleDelete = (index) => {
        const id = gallery.at(index).id;
        onDelete(id);
    };

    const handlePin = (index) => {
        if (isFunction(onMoveGallery)) onMoveGallery([dayId, planId, roomId], index, 0);
        if (isFunction(onMoveGalleryPlace)) onMoveGalleryPlace(location, type, index, placeId);
    };

    const dragEvents = {
        onDragEnter,
        onMouseOver,
        onMouseOut
    };

    useEffect(() => {
        setActiveImage(null);
    }, [planId]);

    const ref = useRef();
    const [previewDimensions, setPreviewDimensions] = useState({ width: 0, height: 0 });
    useEffect(() => {
        if (ref.current) {
            setPreviewDimensions({
                width: ref.current.clientWidth,
                height: ref.current.clientHeight
            });
        }
    }, [ref.current]);

    return (
        <React.Fragment>
            <div className="w-full relative aspect-video grid rounded-md p-[1px]" ref={ref} {...dragEvents}>
                <SVGBackground
                    animate={isDragging}
                    complete={hasFiles}
                    appearAsButton={isHovering}
                    rx={6}
                    className={addClasses('absolute top-0 left-0 h-full w-full overflow-hidden')}
                />
                <div className="absolute top-0 left-0 flex items-center w-full h-full overflow-hidden rounded-md">
                    {hasFiles ? (
                        <React.Fragment>
                            {activeImage && (
                                <ImagePreview
                                    key={activeImage?.id}
                                    imageSrc={activeImage?.src}
                                    alt={activeImage?.alt}
                                    {...previewDimensions}
                                    initialState={activeImage?.coords}
                                />
                            )}

                            {activeImage && isUploading && (
                                <div className="absolute left-0 top-0 w-full h-full flex justify-center items-center bg-[#0005] pointer-events-none">
                                    <Spinner />
                                </div>
                            )}

                            {isHovering && (
                                <>
                                    {!isUploading && (
                                        <div
                                            className="transition-all duration-300 absolute h-full w-full top-0 left-0 flex justify-center items-center rounded-md bg-[#00000044] opacity-0 hover:opacity-100 text-neutral-100 cursor-pointer"
                                            onClick={showModal}
                                        >
                                            <span className="flex items-end gap-2">
                                                <PhotoIcon className="w-8 h-8" />
                                                <span className="text-center">{helperText}</span>
                                            </span>
                                        </div>
                                    )}
                                    <Button
                                        className={addClasses(`absolute left-4`)}
                                        look="circular"
                                        size="xs"
                                        onClick={showPrevImage}
                                    >
                                        <Icon
                                            icon="material-symbols:arrow-left-alt-rounded"
                                            width="1.2rem"
                                            height="1.2rem"
                                        />
                                    </Button>
                                    <Button
                                        className={addClasses(`absolute right-4`)}
                                        look="circular"
                                        size="xs"
                                        onClick={showNextImage}
                                    >
                                        <Icon
                                            icon="material-symbols:arrow-right-alt-rounded"
                                            width="1.2rem"
                                            height="1.2rem"
                                        />
                                    </Button>
                                </>
                            )}
                            {activeImage && (
                                <div className="absolute -translate-x-1/2 left-1/2 bottom-3">
                                    <span className="text-neutral-100">
                                        {activeIndex + 1} / {gallery.length}
                                    </span>
                                </div>
                            )}
                        </React.Fragment>
                    ) : (
                        <div
                            className="flex flex-col items-center justify-center w-full h-full space-y-1 cursor-pointer text-neutral-600"
                            onClick={showModal}
                        >
                            <div className="flex items-center justify-center gap-x-2">
                                <PhotoIcon className="w-6 h-6" />{' '}
                                <span className="text-sm font-semibold">{defaultText}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {
                <GalleryPanel
                    {...props}
                    initialActiveIndex={activeIndex}
                    defaultState={gallery.length > 0 ? 'editing' : 'selecting'}
                    multiple={true}
                    width={480}
                    height={270}
                    files={gallery}
                    isOpen={modal}
                    onClose={showModal}
                    onChangePreview={onChange}
                    onDelete={handleDelete}
                    onPin={handlePin}
                />
            }
        </React.Fragment>
    );
}

function SVGBackground(props) {
    const { animate, complete, appearAsButton, strokeWidth = 2, rx = 6, className, ...rest } = props;

    const rectClassName = cn(
        'transition-all animate-dash',
        appearAsButton ? 'fill-white' : 'fill-neutral-50',
        animate ? 'animation-running fill-primary-100 stroke-primary-300' : 'animation-pause stroke-neutral-300'
    );

    const isSolid = appearAsButton || (complete && !animate);

    return (
        <svg
            preserveAspectRatio="none"
            overflow="visible"
            height={rx === 100 ? '200px' : '0'}
            width={rx === 100 ? '200px' : '0'}
            className={mergeClasses(cn(appearAsButton && rx !== 100 && 'shadow-md', 'rounded-md'), className)}
            {...rest}
        >
            <rect
                className={rectClassName}
                width="100%"
                height="100%"
                style={{ '--stroke-dashoffset': strokeWidth * 6 }}
                vectorEffect="non-scaling-stroke"
                rx={rx}
                strokeWidth={appearAsButton ? 1 : strokeWidth}
                strokeDasharray={isSolid ? 'none' : strokeWidth * 3}
            />
        </svg>
    );
}
